import { createSelector, current } from '@reduxjs/toolkit';
import { isEmptyObj } from '../utilities/common';
import {
  isCoverStandard,
  isUnderwritingDecision,
  isCoverDeclined,
} from '../utilities/application/enquiryHelper';

const getDecisionLoading = (state) => state.decision.loading;
export const getQuoteApplicants = (state) =>
  state.decision.quoteDecisions
    ? state.decision.quoteDecisions.quoteApplicants
    : [];
export const getDecisionApplicants = (state) =>
  state.decision.quoteDecisions
    ? state.decision.quoteDecisions.quoteApplicants
    : [];
export const getProductDecisionApplicants = (state) =>
  state.decision.productDecisions;
export const getEditingDecision = (state) => state.decision.editing;

export const getDeleteApplicantLoading = (state) =>
  state.decision.deletingApplicant;

export const getPremium = (state) => {
  try {
    return state.decision.quoteDecisions.premium;
  } catch {
    return null;
  }
};

export const getInitialCommission = (state) => {
  try {
    return state.decision.quoteDecisions.initialCommission;
  } catch {
    return null;
  }
};

const getDecisionLoadingSelector = createSelector(
  [getDecisionLoading],
  (getDecisionLoading) => {
    return getDecisionLoading;
  }
);

export const getDecisionCoversByApplicantId = createSelector(
  [getDecisionApplicants, (state, applicantId) => applicantId],
  (quoteApplicants, applicantId) => {
    const quoteApplicant = quoteApplicants.find(
      (applicant) => applicant.id === applicantId
    );
    return quoteApplicant ? quoteApplicant.quoteCovers : [];
  }
);

export const getDecisionQuoteApplicantsPremiums = createSelector(
  getDecisionApplicants,
  (quoteApplicants) => {
    if (quoteApplicants) {
      return quoteApplicants.map((quoteApplicant) => ({
        premium: quoteApplicant.premium,
      }));
    }
    return null;
  }
);

export const getValidTermsDecision = createSelector(
  getDecisionApplicants,
  (quoteApplicants) => {
    if (quoteApplicants) {
      return (
        quoteApplicants.filter(
          (quoteApplicant) =>
            quoteApplicant.quoteCovers.findIndex((cover) =>
              isCoverStandard(cover.decision)
            ) !== -1
        ).length > 0
      );
    }
    return false;
  }
);

/**
 * Check if all covers are declined for all applicants
 *
 * @param {Object} state
 * @returns Bool
 */
export const getInvalidTermsDecision = createSelector(
  getDecisionApplicants,
  (quoteApplicants) => {
    if (quoteApplicants && quoteApplicants.length > 0) {
      return (
        quoteApplicants.filter(
          (quoteApplicant) =>
            quoteApplicant.quoteCovers.filter(
              (cover) => !isCoverDeclined(cover.decision)
            ).length > 0
        ).length === 0
      );
    }
    return false;
  }
);

/**
 * Check if one of the applicants has all declined covers
 *
 * @param {Object} state
 * @returns Bool
 */
export const getInvalidApplicantTermsDecision = createSelector(
  getDecisionApplicants,
  (quoteApplicants) => {
    if (quoteApplicants && quoteApplicants.length > 0) {
      return (
        quoteApplicants.filter(
          (quoteApplicant) =>
            quoteApplicant.quoteCovers.filter(
              (cover) => !isCoverDeclined(cover.decision)
            ).length === 0
        ).length > 0
      );
    }
    return false;
  }
);

export const getUWTermsDecision = createSelector(
  getDecisionApplicants,
  (quoteApplicants) => {
    if (quoteApplicants) {
      return (
        quoteApplicants.filter(
          (quoteApplicant) =>
            quoteApplicant.quoteCovers.findIndex((cover) =>
              isUnderwritingDecision(cover.decision)
            ) !== -1
        ).length > 0
      );
    }
    return false;
  }
);

export const getUWTermsProductDecision = createSelector(
  getProductDecisionApplicants,
  (decisionApplicants) => {
    if (decisionApplicants && !isEmptyObj(decisionApplicants)) {
      return (
        decisionApplicants.filter(
          (decisionApplicant) =>
            decisionApplicant.productDecisions.findIndex((product) =>
              isUnderwritingDecision(product.decision)
            ) !== -1
        ).length > 0
      );
    }
    return false;
  }
);

export const getDecisionApplicantIndex = createSelector(
  [getDecisionApplicants, (state, applicantId) => applicantId],
  (applicants, applicantId) =>
    applicants.findIndex((applicant) => applicant.id === applicantId)
);

export const getDecisionApplicantIndexFromMeta = (state, action) => {
  let applicantId = action.meta.arg.applicantId
    ? action.meta.arg.applicantId
    : action.meta.arg;

  let index = getDecisionApplicantIndex(
    { decision: current(state) },
    applicantId
  );
  return index;
};

export default {
  getDecisionLoadingSelector,
};
